import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { CalendarX2, Search, RotateCcw, Ban, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

const REASONS = ["Patient request", "Patient no-show", "Illness", "Weather / travel", "Staff unavailable", "Duplicate booking", "Other"];

export const Cancellations = ({ appointments, patients, serviceTypes, onRefresh, onRebook }) => {
  const [q, setQ] = useState('');
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelled, setCancelled] = useState([]);

  const load = useCallback(() => { axios.get(`${API}/appointments/cancelled`).then(r => setCancelled(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, appointments]);

  const upcoming = useMemo(() => {
    const now = Date.now(); const s = q.trim().toLowerCase();
    return appointments
      .filter(a => !['cancelled', 'completed'].includes(a.status) && !a.checked_out_at && new Date(a.start_datetime) >= now)
      .map(a => { const p = patients.find(x => x.id === a.patient_id); const sv = serviceTypes.find(x => x.id === a.service_type_id); return { ...a, pname: p ? `${p.first_name} ${p.last_name}` : 'Unknown', sname: sv?.name || 'IV Therapy', phone: p?.phone }; })
      .filter(a => !s || `${a.pname} ${a.sname} ${a.start_datetime}`.toLowerCase().includes(s))
      .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime)).slice(0, 60);
  }, [appointments, patients, serviceTypes, q]);

  const confirmCancel = async () => {
    setSaving(true);
    try {
      const full = note.trim() ? `${reason} — ${note.trim()}` : reason;
      await axios.post(`${API}/appointments/${target.id}/cancel`, { reason: full, notify_patient: notify });
      toast.success(`Cancelled ${target.pname}'s appointment${notify ? ' · patient notified' : ''}`);
      setTarget(null); setNote(''); setReason(REASONS[0]); setNotify(true);
      onRefresh?.(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Could not cancel'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="cancellations">
      <div>
        <h2 className="text-2xl font-bold brand-heading gold-gradient-text flex items-center gap-2"><CalendarX2 className="w-6 h-6 text-primary" />Appointment Cancellation</h2>
        <p className="text-xs text-muted-foreground mt-1">Cancel any upcoming visit with a reason (patient is notified by email/text), and review every cancellation with one-click rebooking.</p>
      </div>

      <section className="rounded-xl border-2 border-primary/50 bg-black/30 p-4 space-y-3">
        <p className="text-base font-extrabold uppercase tracking-wide text-primary">Cancel an upcoming appointment</p>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by patient, service or date…" className="pl-9" data-testid="cancel-search" />
        </div>
        <div className="max-h-[320px] overflow-y-auto divide-y divide-border/30 rounded-lg border border-border/40">
          {upcoming.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No upcoming appointments{q ? ' match' : ''}.</p>}
          {upcoming.map(a => (
            <div key={a.id} className="flex items-center justify-between gap-3 p-3 hover:bg-primary/5" data-testid={`cancel-row-${a.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{a.pname} <span className="text-muted-foreground font-normal">· {a.sname}</span></p>
                <p className="text-xs text-muted-foreground">{format(parseISO(a.start_datetime), 'EEE, MMM d · h:mm a')}{a.status === 'checked_in' ? ' · checked in' : ''}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTarget(a)} className="h-8 text-xs border-rose-500/40 text-rose-300 hover:bg-rose-500/10 shrink-0" data-testid={`cancel-btn-${a.id}`}><Ban className="w-3.5 h-3.5 mr-1" />Cancel</Button>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-base font-extrabold uppercase tracking-wide text-primary">Cancelled appointments <Badge variant="outline" className="ml-1 border-primary/40 text-primary" data-testid="cancelled-count">{cancelled.length}</Badge></div>
        <div className="rounded-xl border border-border/50 divide-y divide-border/30 overflow-hidden">
          {cancelled.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No cancellations yet.</p>}
          {cancelled.map(c => (
            <div key={c.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`cancelled-row-${c.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-semibold text-sm">{c.patient_name} <span className="text-muted-foreground font-normal">· {c.service_name}</span>{c.rebooked_appointment_id && <Badge className="ml-2 bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px]">Rebooked</Badge>}</div>
                <p className="text-xs text-muted-foreground">Was {format(parseISO(c.start_datetime), 'EEE, MMM d · h:mm a')} · cancelled {c.cancelled_at ? format(parseISO(c.cancelled_at), 'MMM d, h:mm a') : '—'} by {c.cancelled_by_name || 'staff'}</p>
                <p className="text-xs text-rose-200/90 mt-0.5">Reason: {c.cancellation_reason || '—'}{c.cancel_notified ? ' · patient notified' : ''}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 flex gap-3">{c.patient_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.patient_phone}</span>}{c.patient_email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.patient_email}</span>}</p>
              </div>
              {!c.rebooked_appointment_id && <Button size="sm" onClick={() => onRebook(c)} className="h-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid={`rebook-btn-${c.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Rebook</Button>}
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel appointment</DialogTitle>
            <DialogDescription>{target && <>{target.pname} · {target.sname} · {format(parseISO(target.start_datetime), 'EEE, MMM d · h:mm a')}</>}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <div className="flex flex-wrap gap-1.5">
                {REASONS.map(r => <button key={r} type="button" onClick={() => setReason(r)} data-testid={`cancel-reason-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`} className={`text-xs px-2.5 py-1 rounded-full border ${reason === r ? 'border-primary bg-primary/15 text-primary' : 'border-border/50 text-muted-foreground hover:border-primary/50'}`}>{r}</button>)}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Details (optional)</Label><Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Anything to record on the chart…" data-testid="cancel-note" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={notify} onCheckedChange={v => setNotify(!!v)} data-testid="cancel-notify" />Notify patient by email / text</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Keep appointment</Button>
            <Button onClick={confirmCancel} disabled={saving} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="cancel-confirm">{saving ? 'Cancelling…' : 'Cancel appointment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
